/**
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * HIỆU ỨNG ULTIMATE CỦA ARENA — MÁY BAY · BOM XĂNG · VÙNG CHÁY (tầng vẽ, Phaser)
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * LỖI NGƯỜI DÙNG BÁO (02/10/2026), NGUYÊN VĂN:
 *   · *"Sao trong arena army ult không thấy máy bay"*;
 *   · *"street không thấy tự di chuyển khắp nơi ném bom xăng"*;
 *   · *"Tham khảo skill, ảnh skill ở vượt ải"*.
 *
 * NGUYÊN NHÂN ĐO ĐƯỢC — KHÔNG PHẢI LỖI LUẬT SIM:
 *   · sim ĐÃ di chuyển máy bay (`tickAirstrike`) và ĐÃ ném bom (`tickFirebomb`), nhưng chỉ đẩy vài
 *     `castFx` (một VÒNG TRÒN + chớp sáng) xuống scene ⇒ mắt người chơi chỉ thấy vòng nổ, không bao
 *     giờ thấy "vật thể bay ngang trời" hay "chai xăng đang rơi";
 *   · `ArenaWorld.arenaUltVisuals` (máy bay + bom) và `fireZonesActive` (vùng cháy) là kênh dữ liệu
 *     sim đã phát cho việc vẽ, nhưng **chưa có lớp nào đọc** ⇒ dữ liệu rơi vào hư không.
 *
 * CÁCH LÀM — BÁM ĐÚNG CHẾ ĐỘ ĐI ẢI (theo yêu cầu *"tham khảo skill, ảnh skill ở vượt ải"*):
 *   · dùng CHÍNH 2 lưới ảnh Gemini mà `render/fx.ts` (FxLayer của vượt ải) đã dùng — `maybay` và
 *     `xebom` — qua `loadUltFxSheet`/ `aiCell` (một đường nạp, không chép chuẩn chroma-key);
 *   · cỡ/ cách dựng hình lấy theo tỉ lệ của `FxLayer.renderBursts` (máy bay là "vật thể bay ngang
 *     trời", bom xăng cỡ bàn tay), chỉ hiệu chỉnh theo thang thế giới Arena (1000×600);
 *   · vùng cháy vẽ theo `render/fx.ts: renderFire` (ellipse cam dẹt theo phối cảnh, mờ dần khi hết).
 *
 * ⚠️ ĐÂY LÀ "DỮ LIỆU CHẾT" — lớp này CHỈ ĐỌC state của sim, không sửa unit/ tài nguyên/ `score()`,
 * không tiêu `rng`. Cùng quy ước `ArenaShot`/ `castFx`: xoá lớp này thì game chạy y như trước, chỉ
 * mất hình.
 *
 * ⚠️ POOL SPRITE, KHÔNG CẤP PHÁT MỖI KHUNG HÌNH: bom rơi 0,5s/quả, máy bay tồn tại liên tục, vùng
 * cháy 4s — cấp phát GameObjects theo nhịp đó là tự bắn vào chân (bài học `ProjectileLayer`).
 */
import Phaser from 'phaser';
import { aiCell, fallbackTexture, loadUltFxSheet, type VfxSheet } from './vfx-textures';

/** PHẦN THẾ GIỚI MÀ LỚP NÀY ĐỌC — khai tối thiểu để nói rõ phụ thuộc (không import `sim/`). */
export interface ArenaUltWorld {
  time: number;
  arenaUltVisuals: {
    planes: ReadonlyArray<{ side: 'left' | 'right'; x: number; y: number; dir: 1 | -1 }>;
    bombs: ReadonlyArray<{ side: 'left' | 'right'; x: number; y: number; radius: number; fire: boolean }>;
  };
  fireZonesActive: ReadonlyArray<{ x: number; y: number; radius: number; until: number; side: 'left' | 'right' }>;
  burningUnits: ReadonlyArray<{ x: number; y: number; radius: number; side: 'left' | 'right'; id: number }>;
}

/** CỠ MÁY BAY (đơn vị world) — "vật thể bay ngang trời", ~1/7 bề rộng sân. */
const PLANE_W = 150;
const PLANE_H = 60;
/** DEPTH: dưới lớp hiệu ứng chiêu của scene (8000) và HUD (9000), trên mọi đơn vị. */
const PLANE_DEPTH = 7500;
const BOMB_DEPTH = 7100;

export class ArenaUltFx {
  private readonly scene: Phaser.Scene;
  private sheets = new Map<string, VfxSheet | null>();
  private planes: Phaser.GameObjects.Image[] = [];
  private bombs: Phaser.GameObjects.Image[] = [];
  private zones: Phaser.GameObjects.Image[] = [];
  private burns: Phaser.GameObjects.Image[] = [];
  /**
   * LỚP DỰ PHÒNG KHI CHƯA CÓ ẢNH AI (`maybay-parts`/`xebom-parts`): máy bay vẽ bằng `Graphics` để
   * "không bao giờ vỡ hình" — cùng tinh thần lùi về vector của cả repo.
   */
  private fallbackGfx: Phaser.GameObjects.Graphics;
  private disposed = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.fallbackGfx = scene.add.graphics().setDepth(PLANE_DEPTH);
    void this.loadSheets();
  }

  private async loadSheets(): Promise<void> {
    for (const art of ['maybay', 'xebom']) {
      const sheet = await loadUltFxSheet(this.scene, art);
      if (this.disposed) return;
      this.sheets.set(art, sheet);
    }
  }

  /** Vẽ lại toàn bộ hiệu ứng theo state HIỆN TẠI của sim (gọi mỗi khung hình). */
  sync(world: ArenaUltWorld, dt: number): void {
    if (this.disposed) return;
    void dt;
    const v = world.arenaUltVisuals;
    this.drawPlanes(v.planes, world.time);
    this.drawBombs(v.bombs);
    this.drawZones(world.fireZonesActive, world.time);
    this.drawBurns(world.burningUnits, world.time);
  }

  // ── MÁY BAY ───────────────────────────────────────────────────────────────

  private drawPlanes(
    planes: ArenaUltWorld['arenaUltVisuals']['planes'],
    time: number,
  ): void {
    this.fallbackGfx.clear();
    while (this.planes.length < planes.length) {
      this.planes.push(
        this.scene.add
          .image(0, 0, fallbackTexture('beam'))
          .setOrigin(0.5, 0.5)
          .setDepth(PLANE_DEPTH)
          .setVisible(false),
      );
    }
    planes.forEach((p, i) => {
      const img = this.planes[i];
      const cell = aiCell(this.sheets.get('maybay') ?? null, 0);
      if (cell) {
        img.setTexture(cell.tex, cell.frame);
        img.setDisplaySize(PLANE_W, PLANE_H);
        img.setTint(0xffffff);
        img.setAlpha(1);
        img.setRotation(0);
      } else {
        // LÙI VỀ VECTOR: thân + cánh + cánh quạt, đủ đọc ra "máy bay" mà không cần ảnh.
        img.setVisible(false);
        this.drawFallbackPlane(p.x, p.y, p.dir, time);
      }
      img.setPosition(p.x, p.y);
      img.setFlipX(p.dir < 0);
      if (cell) img.setVisible(true);
    });
    for (let i = planes.length; i < this.planes.length; i++) this.planes[i].setVisible(false);
  }

  private drawFallbackPlane(x: number, y: number, dir: 1 | -1, time: number): void {
    const g = this.fallbackGfx;
    const w = PLANE_W * 0.5;
    const h = PLANE_H * 0.5;
    g.fillStyle(0xcbd5e1, 0.95);
    g.fillTriangle(x - w * dir, y, x + w * 0.4 * dir, y - h * 0.55, x + w * 0.4 * dir, y + h * 0.55);
    g.fillStyle(0x93c5fd, 0.95);
    g.fillEllipse(x, y, w * 1.5, h * 0.5);
    g.lineStyle(2, 0x1e293b, 0.6);
    g.strokeEllipse(x, y, w * 1.5, h * 0.5);
    // cánh quạt quay — dùng `time` của sim nên tất định
    const spin = Math.sin(time * 30) * h * 0.35;
    g.lineStyle(3, 0xf8fafc, 0.9);
    g.lineBetween(x + w * 0.55 * dir, y - spin, x + w * 0.55 * dir, y + spin);
  }

  // ── BOM ĐANG RƠI ──────────────────────────────────────────────────────────

  private drawBombs(bombs: ArenaUltWorld['arenaUltVisuals']['bombs']): void {
    while (this.bombs.length < bombs.length) {
      this.bombs.push(
        this.scene.add
          .image(0, 0, fallbackTexture('impact'))
          .setOrigin(0.5, 0.5)
          .setDepth(BOMB_DEPTH)
          .setVisible(false),
      );
    }
    bombs.forEach((b, i) => {
      const img = this.bombs[i];
      const cell = aiCell(this.sheets.get('xebom') ?? null, b.fire ? 0 : 1);
      if (cell) img.setTexture(cell.tex, cell.frame);
      else img.setTexture(fallbackTexture('impact'));
      const size = b.radius * 1.4;
      img
        .setPosition(b.x, b.y)
        .setDisplaySize(size, size)
        .setTint(b.fire ? 0xffffff : 0xbfdbfe)
        .setRotation((b.x + b.y) * 0.01)
        .setAlpha(1)
        .setVisible(true);
    });
    for (let i = bombs.length; i < this.bombs.length; i++) this.bombs[i].setVisible(false);
  }

  // ── VÙNG CHÁY (dưới chân đơn vị) + LỬA TRÊN NGƯỜI ĐANG CHÁY ───────────────

  private drawZones(
    zones: ArenaUltWorld['fireZonesActive'],
    time: number,
  ): void {
    void time;
    while (this.zones.length < zones.length) {
      this.zones.push(
        this.scene.add
          .image(0, 0, fallbackTexture('aoe'))
          .setOrigin(0.5, 0.5)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setVisible(false),
      );
    }
    zones.forEach((z, i) => {
      const img = this.zones[i];
      const left = Math.max(0, z.until - time);
      const k = Math.min(1, left / 1.2);
      img
        .setPosition(z.x, z.y)
        .setDisplaySize(z.radius * 2.1, z.radius * 1.05)
        .setTint(0xf97316)
        .setAlpha(0.34 * k + 0.12)
        .setDepth(z.y * 10 - 3)
        .setVisible(true);
    });
    for (let i = zones.length; i < this.zones.length; i++) this.zones[i].setVisible(false);
  }

  private drawBurns(burning: ArenaUltWorld['burningUnits'], time: number): void {
    while (this.burns.length < burning.length) {
      this.burns.push(
        this.scene.add
          .image(0, 0, fallbackTexture('spark'))
          .setOrigin(0.5, 1)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setVisible(false),
      );
    }
    burning.forEach((u, i) => {
      const img = this.burns[i];
      const flick = 0.75 + 0.25 * Math.sin(time * 14 + u.id);
      img
        .setPosition(u.x, u.y + 4)
        .setDisplaySize(u.radius * 2.2 * flick, u.radius * 3.4 * flick)
        .setTint(0xfbbf24)
        .setAlpha(0.62)
        .setDepth(u.y * 10 + 40)
        .setVisible(true);
    });
    for (let i = burning.length; i < this.burns.length; i++) this.burns[i].setVisible(false);
  }

  /** DỌN SẠCH — scene Arena là màn thứ ba, người chơi vào/ ra nhiều lần trong một phiên. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const img of [...this.planes, ...this.bombs, ...this.zones, ...this.burns]) img.destroy();
    this.fallbackGfx.destroy();
    this.planes = [];
    this.bombs = [];
    this.zones = [];
    this.burns = [];
  }
}
